import { Button, Menu, MenuItem } from "@mui/material";
import { saveAs } from "file-saver";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import React, { useEffect, useState } from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import useAuthStore from "../store/useAuthStore";

const RANGES = [
  { key: "All", label: "All Time" },
  { key: "This Week", label: "This Week" },
  { key: "This Month", label: "This Month" },
  { key: "Custom", label: "Custom" },
];

const RecievableSummary = ({
  startDate,
  setStartDate,
  endDate,
  setEndDate,
  dateRange,
  setDateRange,
  visibleRows,
  headCells,
}) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [recievableData, setRecievableData] = useState({
    totalOutstanding: 0,
    dueToday: 0,
    dueWithin30Days: 0,
  });
  const [statsLoading, setStatsLoading] = useState(true);
  const { token } = useAuthStore();

  useEffect(() => {
    const fetchRecievableData = async () => {
      setStatsLoading(true);
      try {
        const response = await fetch("/api/incomeSummary", {
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        });
        const data = await response.json();
        setRecievableData({
          totalOutstanding: data.totalOutstanding || 0,
          dueToday: data.dueToday || 0,
          dueWithin30Days: data.dueWithin30Days || 0,
        });
      } catch (err) {
        console.error("Error fetching recievable summary:", err);
      } finally {
        setStatsLoading(false);
      }
    };
    fetchRecievableData();
  }, [token]);

  const handleRangeSelect = (range) => {
    setDateRange(range);
    const now = new Date();
    const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    if (range === "All") {
      setStartDate(null);
      setEndDate(null);
    } else if (range === "This Month") {
      setStartDate(new Date(now.getFullYear(), now.getMonth(), 1));
      setEndDate(new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59, 999));
    } else if (range === "This Week") {
      const dayOfWeek = todayStart.getDay();
      const diffToMonday = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
      const monday = new Date(todayStart);
      monday.setDate(todayStart.getDate() - diffToMonday);
      const sunday = new Date(monday);
      sunday.setDate(monday.getDate() + 6);
      sunday.setHours(23, 59, 59, 999);
      setStartDate(monday);
      setEndDate(sunday);
    } else if (range === "Custom") {
      if (!startDate) setStartDate(todayStart);
      if (!endDate) {
        const end = new Date(todayStart);
        end.setHours(23, 59, 59, 999);
        setEndDate(end);
      }
    }
  };

  const exportCSV = () => {
    const headers = headCells.map((c) => c.label).join(",");
    const rowData = visibleRows.map((row) =>
      headCells.map((c) => `"${row[c.id] || ""}"`).join(",")
    );
    const blob = new Blob([[headers, ...rowData].join("\n")], { type: "text/csv;charset=utf-8;" });
    saveAs(blob, "incomes.csv");
    setAnchorEl(null);
  };

  const exportPDF = () => {
    const doc = new jsPDF();
    doc.text("Income Report", 14, 10);
    autoTable(doc, {
      head: [headCells.map((c) => c.label)],
      body: visibleRows.map((row) => headCells.map((c) => row[c.id] || "")),
      startY: 20,
    });
    doc.save("incomes.pdf");
    setAnchorEl(null);
  };

  return (
    <div className="bg-white border border-gray-200 rounded-xl shadow-sm mb-6 overflow-hidden">
      <div className="flex">
        <div className="w-1 bg-green-400 flex-shrink-0" />
        <div className="flex-1 px-5 py-4 space-y-4">
          {/* Stats row */}
          <div className="flex flex-wrap items-center gap-6">
            <div className="flex items-center gap-3">
              <div className="bg-green-50 text-green-500 rounded-lg px-3 py-2 font-bold text-sm min-w-[44px] text-center">
                Nu.
              </div>
              <div>
                <p className="text-xs font-medium text-gray-400 uppercase tracking-wider">
                  Total Outstanding
                </p>
                {statsLoading ? (
                  <div className="h-6 w-28 bg-gray-100 animate-pulse rounded mt-1" />
                ) : (
                  <p className="text-xl font-bold text-gray-900">
                    Nu. {recievableData.totalOutstanding.toLocaleString()}
                  </p>
                )}
              </div>
            </div>

            <div className="hidden md:block h-8 w-px bg-gray-200" />

            <div>
              <p className="text-xs font-medium text-gray-400 uppercase tracking-wider">Due Today</p>
              {statsLoading ? (
                <div className="h-5 w-16 bg-gray-100 animate-pulse rounded mt-1" />
              ) : (
                <p className="text-base font-semibold text-green-600">
                  Nu. {recievableData.dueToday.toLocaleString()}
                </p>
              )}
            </div>

            <div className="hidden md:block h-8 w-px bg-gray-200" />

            <div>
              <p className="text-xs font-medium text-gray-400 uppercase tracking-wider">Due in 30 Days</p>
              {statsLoading ? (
                <div className="h-5 w-20 bg-gray-100 animate-pulse rounded mt-1" />
              ) : (
                <p className="text-base font-semibold text-blue-500">
                  Nu. {recievableData.dueWithin30Days.toLocaleString()}
                </p>
              )}
            </div>
          </div>

          {/* Filter + export row */}
          <div className="flex flex-wrap items-center gap-3">
            {/* Pill buttons */}
            <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-1">
              {RANGES.map(({ key, label }) => (
                <button
                  key={key}
                  onClick={() => handleRangeSelect(key)}
                  className={`px-3 py-1.5 rounded-md text-sm font-medium transition-all whitespace-nowrap ${
                    dateRange === key
                      ? "bg-white text-blue-600 shadow-sm"
                      : "text-gray-500 hover:text-gray-700"
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>

            {/* Custom date pickers */}
            {dateRange === "Custom" && (
              <div className="flex items-center gap-2 bg-gray-50 border border-gray-200 rounded-lg px-3 py-1.5">
                <svg
                  className="h-4 w-4 text-gray-400 flex-shrink-0"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                  <line x1="16" y1="2" x2="16" y2="6" />
                  <line x1="8" y1="2" x2="8" y2="6" />
                  <line x1="3" y1="10" x2="21" y2="10" />
                </svg>
                <DatePicker
                  selected={startDate}
                  onChange={(date) => {
                    const d = new Date(date);
                    d.setHours(0, 0, 0, 0);
                    setStartDate(d);
                  }}
                  selectsStart
                  startDate={startDate}
                  endDate={endDate}
                  portalId="root"
                  dateFormat="dd MMM yyyy"
                  className="text-sm bg-transparent border-none focus:outline-none w-24"
                  placeholderText="Start date"
                />
                <span className="text-gray-400 text-xs">—</span>
                <DatePicker
                  selected={endDate}
                  onChange={(date) => {
                    const d = new Date(date);
                    d.setHours(23, 59, 59, 999);
                    setEndDate(d);
                  }}
                  selectsEnd
                  startDate={startDate}
                  endDate={endDate}
                  minDate={startDate}
                  portalId="root"
                  dateFormat="dd MMM yyyy"
                  className="text-sm bg-transparent border-none focus:outline-none w-24"
                  placeholderText="End date"
                />
              </div>
            )}

            {/* Active filter badge */}
            {dateRange !== "All" && dateRange !== "Custom" && (
              <span className="inline-flex items-center gap-1 text-xs font-medium text-blue-600 bg-blue-50 border border-blue-100 rounded-full px-2.5 py-1">
                <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                  <polyline points="20 6 9 17 4 12" />
                </svg>
                Filtered
              </span>
            )}

            {/* Export — pushed to right */}
            <div className="ml-auto">
              <Button
                variant="outlined"
                onClick={(e) => setAnchorEl(e.currentTarget)}
                size="small"
                style={{ textTransform: "none", borderColor: "#d1d5db", color: "#374151" }}
              >
                <svg
                  className="h-4 w-4 mr-1.5"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                  <polyline points="7 10 12 15 17 10" />
                  <line x1="12" y1="15" x2="12" y2="3" />
                </svg>
                Export
              </Button>
              <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={() => setAnchorEl(null)}>
                <MenuItem onClick={exportCSV}>Download as CSV</MenuItem>
                <MenuItem onClick={exportPDF}>Download as PDF</MenuItem>
              </Menu>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RecievableSummary;
